var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/hooks/useStock.ts
import { useCallback as useCallback2, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import isFunction from "lodash/isFunction";

// src/hooks/useObservers.ts
import { useCallback, useRef as useRef2 } from "react";
import invariant3 from "tiny-invariant";

// src/utils/ObserverArray.ts
import invariant from "tiny-invariant";
var ObserverArray = class {
  constructor() {
    this.observers = {};
    this.counter = 0;
    this.isEmpty = /* @__PURE__ */ __name(() => Object.values(this.observers).length === 0, "isEmpty");
  }
  call(message) {
    Object.values(this.observers).forEach((observer) => observer(message));
  }
  add(observer) {
    const key = this.counter++;
    this.observers[key] = observer;
    return key;
  }
  remove(key) {
    invariant(Object.prototype.hasOwnProperty.call(this.observers, key), `Could not remove observer: observer with key ${key} not exists`);
    delete this.observers[key];
  }
};
__name(ObserverArray, "ObserverArray");

// src/utils/pathUtils.ts
import get from "lodash/get";
import set from "lodash/set";
import toPath from "lodash/toPath";
import invariant2 from "tiny-invariant";
var joinPaths = /* @__PURE__ */ __name((...segments) => {
  const filteredSegments = segments.filter((segment) => segment !== ROOT_PATH);
  if (filteredSegments.length === 0) {
    return ROOT_PATH;
  }
  return filteredSegments.join(".");
}, "joinPaths");
function normalizePath(path) {
  return path === ROOT_PATH ? path : toPath(path).join(".").trim();
}
__name(normalizePath, "normalizePath");
var isInnerPath = /* @__PURE__ */ __name((_basePath, _path) => {
  if (_basePath === ROOT_PATH)
    return true;
  if (_path === ROOT_PATH)
    return false;
  const path = normalizePath(_path);
  const basePath = normalizePath(_basePath);
  return path.indexOf(basePath + ".") === 0 && path.replace(basePath, "").trim().length > 0;
}, "isInnerPath");
var getOrReturn = /* @__PURE__ */ __name((object, path) => {
  if (path === ROOT_PATH) {
    return object;
  } else {
    return get(object, path);
  }
}, "getOrReturn");
var setOrReturn = /* @__PURE__ */ __name((object, path, value) => {
  if (path === ROOT_PATH) {
    return value;
  } else {
    return set(object, path, value);
  }
}, "setOrReturn");
var longestCommonPath = /* @__PURE__ */ __name((paths) => {
  if (paths.length === 0)
    return ROOT_PATH;
  if (paths.length === 1)
    return normalizePath(paths[0]);
  const sortedPaths = paths.sort();
  const firstPath = toPath(sortedPaths[0]);
  const lastPath = toPath(sortedPaths[sortedPaths.length - 1]);
  for (let i = 0; i < firstPath.length; i++) {
    if (firstPath[i] !== lastPath[i]) {
      return firstPath.slice(0, i).join(".") || ROOT_PATH;
    }
  }
  return firstPath.join(".");
}, "longestCommonPath");
var relativePath = /* @__PURE__ */ __name((_basePath, _subPath) => {
  if (_basePath === ROOT_PATH && _subPath === ROOT_PATH) {
    return ROOT_PATH;
  }
  if (_basePath === ROOT_PATH) {
    return _subPath;
  }
  const basePath = normalizePath(_basePath);
  invariant2(_subPath !== ROOT_PATH, `ROOT_PATH symbol cannot be sub path of any path ("${basePath}")`);
  const subPath = normalizePath(_subPath);
  invariant2(basePath.length > 0 && subPath.indexOf(basePath) === 0, `"${subPath}" is not sub path of "${basePath}"`);
  if (basePath === subPath) {
    return ROOT_PATH;
  } else {
    return subPath.replace(basePath + ".", "");
  }
}, "relativePath");

// src/utils/useLazyRef.ts
import { useRef } from "react";
var useLazyRef = /* @__PURE__ */ __name((initializer) => {
  const ref = useRef(null);
  if (ref.current === null) {
    ref.current = initializer();
  }
  return ref;
}, "useLazyRef");

// src/hooks/useObservers.ts
var ROOT_PATH = Symbol();
var useObservers = /* @__PURE__ */ __name(() => {
  const observers = useRef2({});
  const batchUpdateObservers = useLazyRef(() => new ObserverArray());
  const getObserversKeys = useCallback(() => [
    ...Object.keys(observers.current),
    ...Object.getOwnPropertySymbols(observers.current)
  ], []);
  const batchUpdate = useCallback((update) => {
    batchUpdateObservers.current.call(update);
  }, [batchUpdateObservers]);
  const observeBatchUpdates = useCallback((observer) => batchUpdateObservers.current.add(observer), [batchUpdateObservers]);
  const stopObservingBatchUpdates = useCallback((key) => batchUpdateObservers.current.remove(key), [
    batchUpdateObservers
  ]);
  const observe = useCallback((path, observer) => {
    path = normalizePath(path);
    if (!Object.prototype.hasOwnProperty.call(observers.current, path)) {
      observers.current[path] = new ObserverArray();
    }
    return observers.current[path].add(observer);
  }, []);
  const stopObserving = useCallback((path, observerKey) => {
    path = normalizePath(path);
    const currentObservers = observers.current[path];
    invariant3(currentObservers, "Cannot remove observer from value, which is not observing");
    currentObservers.remove(observerKey);
    if (currentObservers.isEmpty())
      delete observers.current[path];
  }, []);
  const watch = useCallback((path, observer) => {
    const key = observe(path, observer);
    return () => stopObserving(path, key);
  }, [observe, stopObserving]);
  const watchAll = useCallback((observer) => watch(ROOT_PATH, observer), [watch]);
  const watchBatchUpdates = useCallback((observer) => {
    const key = observeBatchUpdates(observer);
    return () => stopObservingBatchUpdates(key);
  }, [observeBatchUpdates, stopObservingBatchUpdates]);
  const isObserved = useCallback((path) => Object.prototype.hasOwnProperty.call(observers.current, normalizePath(path)), []);
  const notifyPaths = useCallback((origin, paths, values) => {
    batchUpdate({ paths, origin, values });
    paths.forEach((path) => {
      const observer = observers.current[path];
      const subValue = getOrReturn(values, path);
      observer.call(subValue);
    });
  }, [batchUpdate]);
  const notifySubTree = useCallback((path, values) => {
    path = normalizePath(path);
    const subPaths = getObserversKeys().filter((tempPath) => isInnerPath(path, tempPath) || path === tempPath || isInnerPath(tempPath, path));
    notifyPaths(path, subPaths, values);
  }, [notifyPaths, getObserversKeys]);
  const notifyAll = useCallback((values) => notifyPaths(ROOT_PATH, getObserversKeys(), values), [
    notifyPaths,
    getObserversKeys
  ]);
  return {
    watch,
    watchAll,
    watchBatchUpdates,
    isObserved,
    notifySubTree,
    notifyAll
  };
}, "useObservers");

// src/hooks/useStock.ts
var useStock = /* @__PURE__ */ __name(({ initialValues }) => {
  const values = useLazyRef(() => cloneDeep(initialValues));
  const { notifySubTree, notifyAll, watch, watchAll, watchBatchUpdates, isObserved } = useObservers();
  const setValue = useCallback2((path, action) => {
    path = normalizePath(path);
    const value = isFunction(action) ? action(getOrReturn(values.current, path)) : action;
    values.current = setOrReturn(values.current, path, value);
    notifySubTree(path, values.current);
  }, [values, notifySubTree]);
  const setValues = useCallback2((newValues) => {
    values.current = newValues;
    notifyAll(newValues);
  }, [values, notifyAll]);
  const getValue = useCallback2((path) => getOrReturn(values.current, path), [
    values
  ]);
  const getValues = useCallback2(() => values.current, [values]);
  const resetValues = useCallback2(() => setValues(cloneDeep(initialValues)), [initialValues, setValues]);
  const stock = useMemo(() => ({
    getValue,
    getValues,
    setValue,
    setValues,
    resetValues,
    watch,
    watchAll,
    watchBatchUpdates,
    isObserved
  }), [getValue, getValues, setValue, setValues, resetValues, watch, watchAll, watchBatchUpdates, isObserved]);
  return stock;
}, "useStock");

// src/hooks/useStockContext.ts
import { useContext } from "react";
import invariant5 from "tiny-invariant";

// src/components/StockContext.ts
import { createContext } from "react";
var StockContext = createContext(void 0);

// src/components/StockRoot.tsx
import React from "react";
var StockRoot = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, stockConfig = __objRest(_b, ["children"]);
  const stock = useStock(stockConfig);
  return /* @__PURE__ */ React.createElement(StockContext.Provider, {
    value: stock
  }, children);
}, "StockRoot");

// src/components/ProxyContext.ts
import { createContext as createContext2 } from "react";
var ProxyContext = createContext2(void 0);

// src/components/ProxyProvider.tsx
import React2 from "react";
var ProxyProvider = /* @__PURE__ */ __name(({ proxy, children }) => {
  const stock = useStockContext();
  return /* @__PURE__ */ React2.createElement(StockContext.Provider, {
    value: stock
  }, /* @__PURE__ */ React2.createElement(ProxyContext.Provider, {
    value: proxy
  }, children));
}, "ProxyProvider");

// src/utils/useInterceptors.ts
import { useCallback as useCallback3, useEffect } from "react";
import cloneDeep2 from "lodash/cloneDeep";
import unset from "lodash/unset";
import invariant4 from "tiny-invariant";
var shouldUseProxy = /* @__PURE__ */ __name((proxy, path) => {
  var _a, _b, _c, _d;
  return proxy && (isInnerPath(proxy.path, path) || ((_b = (_a = normalizePath(proxy.path)).trim) == null ? void 0 : _b.call(_a)) === ((_d = (_c = normalizePath(path)).trim) == null ? void 0 : _d.call(_c)));
}, "shouldUseProxy");
var intercept = /* @__PURE__ */ __name((proxy, path, standardCallback, proxiedCallback, args) => {
  if (!shouldUseProxy(proxy, path)) {
    return standardCallback(...args);
  } else {
    return proxiedCallback(...args);
  }
}, "intercept");
var useInterceptors = /* @__PURE__ */ __name((stock, proxy) => {
  const { watch, setValue, getValue, setValues, getValues } = stock;
  useEffect(() => invariant4(!proxy || proxy.isActive(), "Cannot use not activated proxy. Maybe you forgot to call proxy.activate()?"), [proxy]);
  const interceptedWatch = useCallback3((path, observer) => intercept(proxy, path, watch, (path2, observer2) => proxy.watch(path2, observer2, watch), [path, observer]), [watch, proxy]);
  const interceptedSetValue = useCallback3((path, value) => intercept(proxy, path, setValue, (path2, value2) => proxy.setValue(path2, value2, setValue), [path, value]), [proxy, setValue]);
  const interceptedGetValue = useCallback3((path) => intercept(proxy, path, getValue, (path2) => proxy.getValue(path2, getValue), [
    path
  ]), [proxy, getValue]);
  const interceptedGetValues = useCallback3(() => {
    const allValues = cloneDeep2(getValues());
    const proxiedValue = proxy.getValue(proxy.path, getValue);
    setOrReturn(allValues, proxy.path, proxiedValue);
    return allValues;
  }, [proxy, getValues, getValue]);
  const interceptedSetValues = useCallback3((values) => {
    const proxiedValue = getOrReturn(values, proxy.path);
    unset(values, proxy.path);
    proxy.setValue(proxy.path, proxiedValue, (path, value) => {
      setOrReturn(values, path, value);
    });
    setValues(values);
  }, [proxy, setValues]);
  if (!proxy) {
    return stock;
  }
  return __spreadProps(__spreadValues({}, stock), {
    watch: interceptedWatch,
    setValue: interceptedSetValue,
    getValue: interceptedGetValue,
    getValues: interceptedGetValues,
    setValues: interceptedSetValues
  });
}, "useInterceptors");

// src/hooks/useStockContext.ts
var useStockContext = /* @__PURE__ */ __name((stock, proxy) => {
  const contextStock = useContext(StockContext);
  const contextProxy = useContext(ProxyContext);
  stock = stock != null ? stock : contextStock;
  proxy = proxy != null ? proxy : contextProxy;
  invariant5(stock, "You're trying to access stock outside StockContext and without providing custom stock.");
  return useInterceptors(stock, proxy);
}, "useStockContext");

// src/hooks/useStockValue.ts
import { useEffect as useEffect2, useReducer } from "react";
var useStockValue = /* @__PURE__ */ __name((path, customStock, proxy) => {
  const stock = useStockContext(customStock, proxy);
  const { watch, getValue } = stock;
  const [, forceUpdate] = useReducer((val) => val + 1, 0);
  const value = useLazyRef(() => getValue(path));
  useEffect2(() => watch(path, (newValue) => {
    value.current = newValue;
    forceUpdate();
  }), [path, watch, value]);
  return value.current;
}, "useStockValue");

// src/hooks/useStockState.ts
import { useCallback as useCallback4 } from "react";
var useStockState = /* @__PURE__ */ __name((path, customStock, proxy) => {
  const stock = useStockContext(customStock, proxy);
  const value = useStockValue(path, customStock, proxy);
  const { setValue } = stock;
  const set2 = useCallback4((action) => setValue(path, action), [path, setValue]);
  return [value, set2];
}, "useStockState");

// src/hooks/useAllStockValues.ts
var useAllStockValues = /* @__PURE__ */ __name((customStock, proxy) => {
  return useStockValue(ROOT_PATH, customStock, proxy);
}, "useAllStockValues");

// src/hooks/useMappingProxy.ts
import { useMemo as useMemo2 } from "react";

// src/typings/MappingProxy.ts
import isNil from "lodash/isNil";
import invariant6 from "tiny-invariant";

// src/typings/StockProxy.ts
var StockProxy = class {
  constructor(path) {
    this.activate = /* @__PURE__ */ __name(() => Object.freeze(this), "activate");
    this.isActive = /* @__PURE__ */ __name(() => Object.isFrozen(this), "isActive");
    this.path = path;
  }
};
__name(StockProxy, "StockProxy");

// src/typings/MappingProxy.ts
var MappingProxy = class extends StockProxy {
  constructor(map, path) {
    super(path);
    this.setValue = /* @__PURE__ */ __name((path, value, defaultSetValue) => {
      path = relativePath(this.path, path);
      const innerPaths = Object.entries(this.map).filter(([to]) => isInnerPath(path, to) || path === to);
      innerPaths.sort((a, b) => a.length - b.length).forEach(([to, from]) => from !== void 0 && defaultSetValue(from, getOrReturn(value, relativePath(path, to))));
    }, "setValue");
    this.watch = /* @__PURE__ */ __name((path, observer, defaultWatch) => {
      const proxiedPath = this.getNormalPath(path);
      return defaultWatch(proxiedPath, (value) => observer(this.mapValue(value, path, proxiedPath)));
    }, "watch");
    this.getValue = /* @__PURE__ */ __name((path, defaultGetValue) => {
      const proxiedPath = this.getNormalPath(path);
      return this.mapValue(defaultGetValue(proxiedPath), path, proxiedPath);
    }, "getValue");
    this.mapValue = /* @__PURE__ */ __name((value, path, proxiedPath) => {
      path = relativePath(this.path, path);
      const innerPaths = Object.entries(this.map).filter(([to]) => isInnerPath(path, to) || path === to);
      return innerPaths.reduce((acc, [to, from]) => setOrReturn(acc, relativePath(path, to), getOrReturn(value, relativePath(proxiedPath, from))), {});
    }, "mapValue");
    this.getProxiedPath = /* @__PURE__ */ __name((path) => {
      var _a;
      const proxiedPath = normalizePath(path);
      const normalPath = (_a = Object.entries(this.map).find(([, from]) => from === proxiedPath)) == null ? void 0 : _a[0];
      invariant6(!isNil(normalPath), "Mapping proxy error: trying to get normal path of proxied path, which is not defined in proxy map");
      return joinPaths(this.path, normalPath);
    }, "getProxiedPath");
    this.getNormalPath = /* @__PURE__ */ __name((path) => {
      const normalPath = relativePath(this.path, path);
      const isIndependent = normalPath in this.map;
      invariant6(isIndependent || Object.keys(this.map).findIndex((proxiedPath) => isInnerPath(normalPath, proxiedPath)) !== -1, "Mapping proxy error: trying to proxy value, which is not defined in proxy map.");
      return isIndependent ? this.map[normalPath] : longestCommonPath(Object.entries(this.map).filter(([to]) => isInnerPath(normalPath, to)).map(([, from]) => from));
    }, "getNormalPath");
    this.map = Object.entries(map).reduce((acc, [key, value]) => {
      acc[key] = normalizePath(value);
      return acc;
    }, {});
  }
};
__name(MappingProxy, "MappingProxy");

// src/hooks/useMappingProxy.ts
var useMappingProxy = /* @__PURE__ */ __name((map, path) => {
  return useMemo2(() => {
    const proxy = new MappingProxy(map, path);
    proxy.activate();
    return proxy;
  }, [path, map]);
}, "useMappingProxy");
export {
  MappingProxy,
  ObserverArray,
  ProxyContext,
  ProxyProvider,
  ROOT_PATH,
  StockContext,
  StockProxy,
  StockRoot,
  getOrReturn,
  intercept,
  isInnerPath,
  joinPaths,
  longestCommonPath,
  normalizePath,
  relativePath,
  setOrReturn,
  useAllStockValues,
  useMappingProxy,
  useObservers,
  useStock,
  useStockContext,
  useStockState,
  useStockValue
};
